import React, {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { TableOnScreenProvider } from 'contexts/TableOnScreenContext';
import { VisibleTileProvider } from 'contexts/VisibleTileContext';
import { windowStartFor } from 'utils/tableViewWidth';

import { arrangeInTableOrder } from 'components/SaloonLobby/tableOrder';

import { createSpringDriver, SpringDriver } from './tableSpring';
import {
  clearSwipeDrag,
  DragSession,
  getDragOffset,
  noteSettleTargetPx,
  publishDragOffset,
  subscribeDragOffset,
  subscribeDragPhase
} from './tableSwipeDrag';
import {
  beginTableSwitch,
  endTableSwitch,
  prefersReducedMotion,
  slideDirection
} from './tableSwitchTransition';

/**
 * Upper bound on a settle, used only for the entrance-fade flag (a spring has
 * no duration to derive one from). Comfortably past where a critically damped
 * 340ms spring has come to rest; the flag is cleared on rest, so this only
 * takes effect if the rAF loop never runs — a backgrounded tab, say.
 */
const SETTLE_DEADLINE_MS = 1200;

/**
 * How many table panels stay mounted (the shown one plus the most recent
 * others). Matches the 4-table open cap: with the cap at 3, paging first ->
 * last -> first across four tables evicted a felt at each end and remounted it
 * on the way back - a full mount (seats, observers, ~100 gsap tween inits,
 * measured) landing INSIDE the gesture window. A parked panel is cheap to
 * keep since background tables render at most once per 2s (see
 * utils/tablePayloadQueue BackgroundTableBuffer); a mid-gesture mount is not.
 */
export const KEEP_ALIVE_TABLES = 4;

/**
 * A panel's whole felt subtree, isolated behind memo. The carousel's own state
 * changes several times per gesture (mounting neighbours at drag start,
 * promoting keep-alives, collapsing on settle) and each one re-rendered EVERY
 * mounted felt - three or four full table subtrees of work per state change,
 * measured as the bulk of the 90-120ms touchend commits. None of that state
 * changes what a panel renders (position and visibility are on the wrapper
 * div), so the subtree only needs to re-render when the render prop itself
 * changes - which the owner arranges to happen exactly when the ACTIVE table
 * changes (see pages/Game.tsx keying its render callback on the gameID).
 */
const PanelContent = React.memo(
  ({ id, render }: { id: string; render: (displayedGameID: string) => ReactNode }) => (
    <>{render(id)}</>
  )
);

/**
 * How long after the carousel comes to REST before the window's immediate
 * neighbours are pre-mounted (hidden, inert — same parking as keep-alive).
 *
 * Keep-alive only holds tables the player has BEEN on this mount — and the
 * whole carousel unmounts on the lobby (SlideContainer's isTableMounted gate),
 * so a lobby round-trip (the add-a-table flow) comes back knowing only the
 * table it opened. The first swipe then dragged in a panel whose table was
 * MOUNTING FROM SCRATCH: a blank backdrop for ~a second before the felt
 * popped in (Jason 8/21 — "blank brown background being swiped in, one second
 * later it loads"). Warming the neighbours at rest means a swipe can only
 * ever reveal a table that is already built — in every flow, not just that
 * one. The delay puts the mount just past the view change itself (every view
 * change is an instant cut since r10 — the travel slides are retired), so the
 * build cost never shares frames with an entrance animation.
 */
export const WARM_NEIGHBOURS_DELAY_MS = 320;

type RebindingTableViewProps = {
  /** The table this view should be showing. */
  gameID: string;
  /**
   * Render the table subtree for the given gameID. Consumers key their
   * per-table providers on this value so the subtree remounts (and resets) on a
   * table change — see pages/Game.tsx.
   */
  children: (displayedGameID: string) => ReactNode;
  /** Fired when the shown table changes (prev → next), for teardown hooks. */
  onRebound?: (prev: string, next: string) => void;
  /**
   * Every open table in add-order — the same order the navigation strip lays
   * its cards out in. The carousel shows a WINDOW onto this list.
   */
  tables?: string[];
  /**
   * How many tables share the viewport. 1 is the phone experience and the
   * default; 2-4 split the width evenly between adjacent tables in `tables`.
   */
  tablesInView?: number;
  /** Index in `tables` of the leftmost table on screen. */
  windowStart?: number;
  /** A gesture paged the window; the owner should store the new start. */
  onWindowStartChange?: (start: number) => void;
  /**
   * The tables that are still open (state.tables). Kept-alive panels for tables
   * no longer in this list are dropped. Optional: without it the keep-alive cap
   * is the only bound (storybook / tests).
   */
  openGameIDs?: string[];
};

/**
 * The single-table surface: a horizontal carousel of the player's open tables.
 *
 * ONE model serves every kind of switch. `panels` are the tables currently on
 * screen, left to right; `restIndex` is the one centred in the viewport. The
 * track is a full-viewport-wide box per panel, moved only by `transform`:
 *
 *      panels: [ prev ][ cur ][ next ]        translate = -restIndex * width
 *
 *   • DRAG — the finger owns the offset. SlideContainer publishes touchmove
 *     deltas through tableSwipeDrag and they are written straight to the
 *     track's style, so the tables follow the thumb with no React work per
 *     frame. Neighbours are mounted when the drag starts.
 *   • SETTLE — on release a SPRING takes the offset over at the exact velocity
 *     the finger left it and carries it to the committed table (or back), then
 *     collapses to one panel. It publishes through the same offset channel the
 *     finger used, so the handover is invisible and anything following the
 *     felt keeps following it.
 *   • TAP / programmatic — a gameID change with no drag in flight mounts the
 *     incoming table off-screen and runs the same spring to the same channel.
 *
 * Three details keep the motion honest, each of which was previously a way the
 * switch read as an instant jump:
 *
 *  1. PANELS ARE ABSOLUTE, NEVER RESIZED — every panel is a full-viewport box
 *     in every state, so a table's felt never re-lays-out (and never re-scales
 *     through its ResizeObserver) when a slide starts or ends.
 *  2. A PROGRAMMATIC SLIDE STARTS ONLY AFTER THE INCOMING TABLE HAS PAINTED.
 *     That mount is expensive (gsap, seats, observers); starting in the same
 *     frame meant the browser painted the end state only.
 *  3. THE MOVEMENT IS A SPRING, not a curve with a duration. A curve animates
 *     toward a target from a snapshot, so it cannot be grabbed and redirected
 *     mid-flight — which forced the old implementation to lock input out for
 *     the length of every settle. That lockout is what a flurry of swipes used
 *     to feel like: not slow motion, but an interface that had stopped
 *     answering. See tableSpring.
 *
 * The outgoing table stays mounted while it leaves and the incoming one mounts
 * off-screen, so its mount (and pages/Game.tsx's suppressed entrance fade) is
 * never seen, and it is not remounted when the movement ends.
 *
 * At rest there is no transform at all: `transform` on an ancestor makes
 * `position: fixed` descendants resolve against it, and the table view has
 * plenty of those. Reduced motion (and any client without Element.animate)
 * swaps instantly, as this component behaved before.
 */
const RebindingTableView = ({
  gameID,
  children,
  onRebound,
  tables,
  tablesInView = 1,
  windowStart = 0,
  onWindowStartChange,
  openGameIDs
}: RebindingTableViewProps) => {
  // Comma-joined so the eviction effect below re-runs when the SET of open
  // tables changes, not on every array identity change from the parent.
  const openGameIDsKey = (openGameIDs ?? []).join(',');

  // The table currently committed to the DOM. Kept in state (not a ref) because
  // a programmatic switch has to be derived DURING RENDER: deriving it in an
  // effect would commit one render of the new table alone first, unmounting the
  // outgoing table and then remounting it for the slide — the blank-and-reopen
  // flash this component exists to remove.
  const [displayed, setDisplayed] = useState(gameID);
  // What is on screen. The window at rest; the window plus its neighbours
  // mid-gesture. `restIndex` is the panel sitting at the viewport's LEFT edge —
  // at one table in view that is simply the table you are on.
  const [view, setView] = useState<{ panels: string[]; restIndex: number } | null>(null);
  // KEEP-ALIVE. Tables the player was recently on stay MOUNTED (parked
  // off-screen, inert) instead of being torn down, so switching back to one
  // costs no remount at all — which is the bulk of a switch's main-thread bill:
  // ~9 seats with their cards/timers/bets, observers and contexts, rebuilt every
  // time. Most recent first; capped so a long session can't hold every table.
  const [keepAlive, setKeepAlive] = useState<string[]>(() => (gameID ? [gameID] : []));
  const trackRef = useRef<HTMLDivElement>(null);
  const safetyRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reboundRef = useRef(gameID);
  // Set while a finger-driven session owns the surface, so a gameID change
  // committed by that gesture doesn't also kick off a programmatic slide.
  const dragOwnedRef = useRef(false);

  // The ordered open tables and how many share the viewport. Defaulting to just
  // the displayed table keeps every existing caller on the exact
  // one-table-in-view behaviour this component started with.
  // Falls back to the displayed table alone whenever the list cannot describe
  // what is on screen — no list supplied (tests, stories), or the displayed
  // table is not in it, which is the moment of leaving for the lobby and the
  // moment a table is being closed. A window onto a list that does not contain
  // you is not a window.
  const order = tables?.length && tables.includes(displayed) ? tables : [displayed];
  const perView = Math.max(1, Math.min(tablesInView, order.length));
  // Width of ONE panel as a percentage of the track. The whole generalisation
  // from "one table fills the viewport" to "N share it" is this number: panels
  // are this wide, sit this far apart, and the track travels this far per step.
  const panelPct = 100 / perView;

  const maxStart = Math.max(0, order.length - perView);
  // Derive the rendered window so it ALWAYS contains the displayed table. The
  // STORED start can lag it by a render (a chained commit that collapsed, a
  // deep link) and the effect below reconciles it - but rendering from the
  // stale value first would unmount the active panel for that render, taking
  // the fixed chrome (header, ticker) with it. Under a long post-commit task
  // that headerless frame stays painted for the task's whole duration -
  // observed on device as the header "flickering away" on fast swipes.
  const start = windowStartFor(
    Math.min(Math.max(0, windowStart), maxStart),
    order.indexOf(displayed),
    perView,
    order.length
  );
  const windowTables = order.slice(start, start + perView);

  // THE GESTURE HANDLERS MUST SEE THE CURRENT LAYOUT. The drag-phase
  // subscription below deliberately survives re-renders (its deps are only
  // [view, settleTo, settleDone] — resubscribing per render could miss a phase
  // emission mid-gesture), which means its handlers close over whatever
  // order/window this component had WHEN THE EFFECT LAST RAN. That froze the
  // panel list at carousel mount: after adding a table from single view the
  // carousel mounts (post-lobby) before the new table's subscription
  // round-trips, the closure captures a one-table order, and the FIRST swipe
  // animated a one-panel track — the outgoing felt slid over bare backdrop
  // while its neighbour sat parked off-track, popping in only at the collapse
  // (Jason 8/21: "the original table doesn't follow it... blank brown
  // background... then it comes into view"). Same latest-values-in-a-ref
  // idiom as useTrackpadTableSwipe.
  const layout = { order, start, perView, maxStart, onWindowStartChange };
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  // The track spans the viewport, so ONE panel's travel is that divided by how
  // many share it. Falls back to the viewport when the track hasn't been
  // measured (or reports 0, as in jsdom) so a slide is never zero-length.
  // Reads the CURRENT perView through layoutRef — this runs inside the
  // long-lived gesture handlers (see above).
  const trackWidth = () =>
    trackRef.current?.getBoundingClientRect().width || window.innerWidth || 0;
  const stepWidth = () => trackWidth() / layoutRef.current.perView;

  // Keep the focused table on screen, moving the window as little as possible —
  // tapping the second of two visible felts must not slide the row under the
  // tap. See windowStartFor.
  const orderKey = order.join(',');
  useEffect(() => {
    const next = windowStartFor(windowStart, order.indexOf(gameID), perView, order.length);
    if (next !== windowStart) onWindowStartChange?.(next);
    // orderKey stands in for `order`, which is a fresh array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameID, perView, orderKey, windowStart]);

  const clearTransform = useCallback(() => {
    const el = trackRef.current;
    if (el) {
      el.style.transform = '';
      el.style.willChange = '';
    }
  }, []);

  /**
   * The spring is home. Ask React to collapse to the settled table — and do NOT
   * release the offset here.
   *
   * The track is still holding the settled position through the offset channel,
   * and both panels are still mounted until React commits the collapse.
   * Publishing a 0 offset now would snap the track back to its parked position
   * for one frame with the outgoing table still on screen: the "previous table
   * flashes" glitch. The layout effect below releases it once the single-panel
   * DOM is in place, which is the same frame ordering the old `fill: forwards`
   * animation relied on.
   */
  const settleDone = useCallback(() => {
    if (safetyRef.current) clearTimeout(safetyRef.current);
    safetyRef.current = null;
    endTableSwitch();
    dragOwnedRef.current = false;
    setView(null);
  }, []);

  /**
   * The one spring that moves this carousel, created once and reused for every
   * settle — a fresh spring per gesture would start from rest each time and
   * throw away the velocity that makes an interrupted swipe feel continuous.
   *
   * It runs in the SAME coordinate space as the finger: `offset` px away from
   * the resting panel, published through the drag channel. So a settle is not a
   * different mechanism from a drag, it is the same number moving under
   * different ownership — which is why every follower (the track, the indicator
   * dot) keeps tracking without knowing whether a finger or the spring is
   * driving.
   */
  const springRef = useRef<SpringDriver | null>(null);
  const getSpring = useCallback((): SpringDriver => {
    if (!springRef.current) {
      springRef.current = createSpringDriver({
        onFrame: publishDragOffset,
        // Collapsing to a single panel is the ONLY React work in a settle, and
        // it happens once the spring is home rather than on a timer.
        onRest: settleDone,
        immediate: prefersReducedMotion
      });
    }
    return springRef.current;
  }, [settleDone]);

  /**
   * Settle the track to `targetOffsetPx`, carrying `velocityPxPerMs` in.
   *
   * Velocity handoff is the seam that used to be visible: a keyframe animation
   * starts from zero speed however fast the gesture was, so the felt perceptibly
   * hesitated at the moment the finger lifted. Handing the release velocity to
   * the spring means the first frame of the settle moves at exactly the speed
   * the last frame of the drag did, and there is no seam to see.
   */
  const settleTo = useCallback(
    (targetOffsetPx: number, velocityPxPerMs = 0) => {
      const spring = getSpring();
      spring.setPosition(getDragOffset(), velocityPxPerMs);
      // Generous deadline: the flag's remaining job is telling pages/Game.tsx to
      // skip its entrance fade, and a spring has no duration to size it from.
      // settleDone clears it the moment the spring rests, so this only matters
      // if the rAF loop is killed outright (backgrounded tab).
      beginTableSwitch(SETTLE_DEADLINE_MS);
      // A spring finishes when it rests, and rest only happens if frames keep
      // arriving. Nothing guarantees they will — rAF is paused in a hidden tab
      // and can be starved by a long task — and a settle that never completes
      // strands TWO tables stacked on screen. The old fixed-duration animation
      // got this for free from its timeout; the spring has to ask for it.
      if (safetyRef.current) clearTimeout(safetyRef.current);
      safetyRef.current = setTimeout(() => {
        spring.stop();
        settleDone();
      }, SETTLE_DEADLINE_MS);
      spring.setTarget(targetOffsetPx);
    },
    [getSpring, settleDone]
  );

  // --- Finger-driven drag -------------------------------------------------
  useEffect(() => {
    const offDrag = subscribeDragPhase((state) => {
      if (state.phase === 'dragging') {
        // A finger owns the track from here. Stop the settle spring and its
        // safety timer — without this, a grab mid-settle left the spring's rAF
        // loop publishing offsets against the finger's (two writers, one
        // channel). Harmless for a near-home spring-back; visibly wrong for a
        // grabbed COMMITTED settle with real distance left.
        if (safetyRef.current) {
          clearTimeout(safetyRef.current);
          safetyRef.current = null;
        }
        springRef.current?.stop();
        dragOwnedRef.current = true;
        // The window plus one neighbour each side — the most a single-step page
        // can reveal. At one table in view this is the familiar
        // [prev][cur][next]; at three it is [prev][a][b][c][next].
        // Read at CALL time via layoutRef — see its comment: the effect-scope
        // values are frozen at the last view change, and a first gesture after
        // an order change must still see every panel.
        const { order: liveOrder, start: liveStart, perView: livePerView } = layoutRef.current;
        const from = Math.max(0, liveStart - 1);
        const to = Math.min(liveOrder.length, liveStart + livePerView + 1);
        beginTableSwitch(60_000); // held down for as long as the finger is
        // Live layout (EN-10545 r9) + keep-alive promotion (EN-10538): the
        // panels this gesture puts on screen join the keep-alive list NOW,
        // not only on commit — a cancelled swipe otherwise unmounted the
        // neighbours it had just mounted, felt as the table "reloading".
        const touched = liveOrder.slice(from, to);
        setView({ panels: touched, restIndex: liveStart - from });
        promoteToKeepAlive(touched);
        return;
      }
      if (state.phase === 'settling') {
        if (!trackRef.current) {
          settleDone();
          return;
        }
        const session: DragSession = state.session;
        // ONE STEP, whatever the direction — a page moves the window by a
        // single table, never by a screenful. Reading the direction from which
        // neighbour was committed to (rather than looking the target up among
        // the panels) is what keeps that true at every table count: at three
        // in view the incoming table is three panels away, but the window only
        // slides one.
        const direction = state.target === null ? 0 : state.target === session.prev ? -1 : 1;
        // Distance is in the finger's own units — how far the offset still has
        // to travel — so committing and springing back are the same operation
        // with different targets, not two paths kept feeling alike by hand.
        // `-0 * width` is -0, which is a real value that then travels down the
        // offset channel to every follower. Spell the spring-back target as a
        // plain 0.
        const targetPx = direction === 0 ? 0 : -direction * stepWidth();
        // Tell the drag channel where this settle is heading, so a gesture
        // grabbing it mid-flight can re-base its coordinates (see
        // beginSwipeDrag). 0 for a spring-back, ± one step for a commit.
        noteSettleTargetPx(targetPx);
        settleTo(targetPx, state.velocityPxPerMs);
        if (direction !== 0) {
          // Current window, not the effect closure's — see layoutRef.
          const live = layoutRef.current;
          live.onWindowStartChange?.(Math.min(Math.max(0, live.start + direction), live.maxStart));
        }
      }
    });

    // Live offset: written straight to the DOM, no React render per frame.
    const offOffset = subscribeDragOffset((dx) => {
      const el = trackRef.current;
      if (!el || !view) return;
      el.style.willChange = 'transform';
      el.style.transform = `translate3d(calc(${-view.restIndex * 100}% + ${dx}px), 0, 0)`;
    });

    return () => {
      offDrag();
      offOffset();
    };
  }, [view, settleTo, settleDone]);

  // --- Programmatic switch (nav-strip tap, exit-to-next, deep link) --------
  if (displayed !== gameID) {
    const from = displayed;
    setDisplayed(gameID);
    // A drag already put this table on screen and is settling onto it — let
    // that animation finish rather than starting a second one.
    const handledByDrag = dragOwnedRef.current;
    // CHAINED page: a second flick committed while this gesture's settle is
    // still in flight (rapid re-swipe). Respect it IMMEDIATELY: extend the
    // panel run if needed and retarget the running spring, so the felt glides
    // through the intermediate table to the new one in a single motion -
    // position and velocity carry through, which is the entire point of a
    // spring. Only same-direction appends glide (the rapid re-swipe case);
    // anything else falls through to the existing collapse-then-show path.
    if (handledByDrag && view && trackRef.current && gameID) {
      const gid = String(gameID);
      let panels = view.panels;
      let restIndex = view.restIndex;
      let idx = panels.indexOf(gid);
      if (idx === -1) {
        const orderIdx = order.indexOf(gid);
        const lastIdx = order.indexOf(panels[panels.length - 1]);
        const firstIdx = order.indexOf(panels[0]);
        if (orderIdx !== -1 && lastIdx !== -1 && orderIdx === lastIdx + 1) {
          panels = [...panels, gid];
          idx = panels.length - 1;
          setView({ panels, restIndex });
        } else if (orderIdx !== -1 && firstIdx !== -1 && orderIdx === firstIdx - 1) {
          // BACKWARD chain — the mirror of the append above, and it was
          // missing: a rapid backward re-swipe (last -> first paging) commits
          // to a table LEFT of the panel run, so with no prepend the spring
          // finished settling onto the INTERMEDIATE table and the view then
          // hard-cut to the committed one — seen on device as "it shows the
          // previous table's players, then snaps". Prepending shifts every
          // slot right one, so restIndex follows to keep the offset's
          // reference panel (and the live transform) meaning the same place
          // on screen; the layout effect below repaints the shifted frame
          // with the current offset before the browser shows it.
          panels = [gid, ...panels];
          idx = 0;
          restIndex = view.restIndex + 1;
          setView({ panels, restIndex });
        }
      }
      if (idx >= 0) {
        beginTableSwitch(); // extend the deadline to cover the longer run
        // Carry the running spring's velocity through the retarget - a glide
        // redirected at zero speed visibly hesitates mid-run.
        settleTo(-(idx - restIndex) * stepWidth(), springRef.current?.velocity() ?? 0);
        // No early return: fall through to the normal render (canSlide below
        // is already false for drag-owned switches, so no second animation).
      } else {
        // The commit can't join this panel run at all (it jumped more than one
        // table, or the run's edge isn't adjacent). Never leave the settle
        // running toward a table that is no longer the destination: no panel
        // would match the committed table, which blanks the fixed chrome
        // (FixedChrome renders nothing with no active panel) until the settle
        // dies on its own. Collapse cleanly instead - the resting window
        // re-derives around the committed table on this very render.
        springRef.current?.stop();
        if (safetyRef.current) clearTimeout(safetyRef.current);
        safetyRef.current = null;
        endTableSwitch();
        dragOwnedRef.current = false;
        setView(null);
      }
    }
    // Nothing to slide between on the first table (no outgoing view), when the
    // table is cleared (leaving for the lobby), or under reduced motion.
    const canSlide = !handledByDrag && !!from && !!gameID && !prefersReducedMotion();
    if (canSlide) {
      // Raised here rather than in an effect so it is already true while the
      // INCOMING table mounts — that mount is a child, and child effects run
      // before this component's, so an effect would be too late for
      // pages/Game.tsx to know to skip its entrance fade.
      beginTableSwitch();
      const direction = slideDirection(from, gameID, tables ?? []);
      setView(
        direction === 'left'
          ? { panels: [from, gameID], restIndex: 0 }
          : { panels: [gameID, from], restIndex: 1 }
      );
    } else if (!handledByDrag) {
      endTableSwitch();
      setView(null);
    }
  }

  // Run a programmatic slide once the incoming table has been laid out and
  // painted — two frames, so its mount is paid for while the track is parked.
  const pendingProgrammatic = !dragOwnedRef.current && view !== null;
  useEffect(() => {
    if (!pendingProgrammatic || !view) return undefined;
    const width = stepWidth();
    const targetIndex = view.restIndex === 0 ? 1 : 0;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => settleTo((view.restIndex - targetIndex) * width));
    });
    return () => {
      cancelAnimationFrame(first);
      if (second) cancelAnimationFrame(second);
    };
    // view identity changes exactly once per programmatic switch.
  }, [pendingProgrammatic, view, settleTo]);

  // Whenever the panel frame changes mid-gesture (a chained glide prepends a
  // panel and shifts restIndex, or a drag mounts its neighbours), re-express
  // the CURRENT offset against the new frame before the browser paints. React
  // writes the parked transform (no offset) whenever restIndex changes, and
  // the next spring/finger frame is a paint too late - a one-frame teleport of
  // the whole felt.
  useLayoutEffect(() => {
    if (!view) return;
    const el = trackRef.current;
    if (!el) return;
    const dx = getDragOffset();
    if (dx !== 0) {
      el.style.willChange = 'transform';
      // Same expression the offset subscription writes every frame, so the
      // repaint and the next frame agree on where the track is.
      el.style.transform = `translate3d(calc(${-view.restIndex * 100}% + ${dx}px), 0, 0)`;
    }
  }, [view]);

  // Teardown, once the collapse to a single panel has been committed. Runs as
  // a LAYOUT effect so the animation is released after the DOM update but
  // before the browser paints — there is no frame in which the track has been
  // dropped back to its parked offset while both tables are still mounted
  // (that frame was the "previous table flashes" glitch).
  useLayoutEffect(() => {
    if (view) return;
    springRef.current?.stop();
    // Safe to release the offset now: the single-panel DOM is committed, so
    // dropping the track's transform cannot expose the table that just left.
    clearSwipeDrag();
    clearTransform();
  }, [view, clearTransform]);

  useEffect(() => {
    const prev = reboundRef.current;
    if (gameID === prev) return;
    reboundRef.current = gameID;
    if (gameID) onRebound?.(prev, gameID);
  }, [gameID, onRebound]);

  /**
   * Add a gesture's (or the pre-warm's) panels to the keep-alive list without
   * reordering the ones already there. Shared by drag-start promotion and the
   * at-rest pre-warm below.
   */
  const promoteToKeepAlive = useCallback((touched: string[]) => {
    setKeepAlive((prev) => {
      const next = [
        ...prev.filter((id) => touched.includes(id)),
        ...touched.filter((id) => !prev.includes(id)),
        ...prev.filter((id) => !touched.includes(id))
      ].slice(0, KEEP_ALIVE_TABLES);
      return next.length === prev.length && next.every((id, i) => id === prev[i]) ? prev : next;
    });
  }, []);

  // Pre-warm the swipe's neighbours while the carousel is AT REST. The first
  // swipe of a session used to reveal a BLANK neighbour: that panel mounted
  // mid-gesture and its first-ever mount also pays image decode (avatars,
  // felt assets), so nothing was ready to paint until the gesture was over.
  // Subsequent swipes looked fine (decode cache). Mounting the neighbours
  // parked-and-hidden shortly after the current table settles moves that
  // one-time cost to an idle moment; the first gesture then reveals a felt
  // that is already rendered, decoded and receiving updates.
  useEffect(() => {
    if (view) return; // gestures own the panel set while one is in flight
    if (order.length < 2) return;
    const id = window.setTimeout(() => {
      const from = Math.max(0, start - 1);
      const to = Math.min(order.length, start + perView + 1);
      promoteToKeepAlive(order.slice(from, to));
    }, 400);
    return () => window.clearTimeout(id);
    // orderKey stands in for `order` (fresh array each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, orderKey, start, perView, promoteToKeepAlive]);

  // Promote the shown table to the front of the keep-alive list.
  useEffect(() => {
    if (!displayed) return;
    setKeepAlive((prev) => {
      if (prev[0] === displayed) return prev;
      return [displayed, ...prev.filter((id) => id !== displayed)].slice(0, KEEP_ALIVE_TABLES);
    });
  }, [displayed]);

  // Drop panels for tables that are no longer open (Exit Table, a balancing move,
  // a closed tournament table): keeping a dead table's subtree mounted would run
  // effects against a slice that no longer exists. Skipped when the caller does
  // not supply the open set (storybook / tests) — the cap still bounds growth.
  useEffect(() => {
    if (!openGameIDsKey) return;
    const open = openGameIDsKey.split(',').filter(Boolean);
    setKeepAlive((prev) => {
      const next = prev.filter((id) => id === displayed || open.includes(id));
      return next.length === prev.length ? prev : next;
    });
  }, [openGameIDsKey, displayed]);

  // Pre-warm the window's immediate neighbours once the carousel is at rest —
  // see WARM_NEIGHBOURS_DELAY_MS. Warmed ids persist through a drag (the drag's
  // own panels take over rendering them) and are replaced, not accumulated,
  // when the window moves: a neighbour that stops being one either lives on in
  // keep-alive (it was shown) or unmounts (it never was).
  const [warmed, setWarmed] = useState<string[]>([]);
  const neighbourKey = view
    ? null // mid-gesture: keep whatever is warm, decide again at rest
    : [order[start - 1], order[start + perView]].filter(Boolean).join(',');
  useEffect(() => {
    if (neighbourKey === null) return undefined;
    if (!neighbourKey) {
      setWarmed((prev) => (prev.length ? [] : prev));
      return undefined;
    }
    const t = setTimeout(() => setWarmed(neighbourKey.split(',')), WARM_NEIGHBOURS_DELAY_MS);
    return () => clearTimeout(t);
  }, [neighbourKey]);

  useEffect(
    () => () => {
      if (safetyRef.current) clearTimeout(safetyRef.current);
      springRef.current?.stop();
      endTableSwitch();
      clearSwipeDrag();
    },
    []
  );

  // At rest the window itself is what is mounted; mid-gesture the view carries
  // the window plus whichever neighbours the drag brought on.
  const panels = view ? view.panels : windowTables;
  // Mounted but not on screen: the keep-alive panels plus the warmed
  // neighbours. They are parked far off to the side (never within the track's
  // travel), hidden and non-interactive, so they cost no paint and cannot be
  // reached — but they keep their DOM, their measured layout and their
  // per-table state, which is what makes switching (back) to them free. They
  // are inert too: see contexts/TableActiveContext. Warmed ids are re-checked
  // against the live order so a table that closes never lingers as a hidden
  // subtree running against a dead slice.
  const held = [...keepAlive, ...warmed.filter((id) => order.includes(id))].filter(
    (id, idx, arr) => id && !panels.includes(id) && arr.indexOf(id) === idx
  );
  const rendered = [...panels, ...held];
  // Keep DOM order fixed to table order, so a panel moving between on-screen
  // and held doesn't re-parent its felt mid-gesture. Closed tables' panels go
  // last. On-screen position comes from `panels.indexOf`, not DOM order.
  const domOrder = arrangeInTableOrder(rendered, order, (id) => id, 'last');
  // Parked at the resting offset until the finger or the spring takes over, so
  // the first painted frame is already in the right place.
  const parked = view ? `translate3d(${-view.restIndex * panelPct}%, 0, 0)` : undefined;

  return (
    <div style={viewportStyle} data-testid='table-carousel'>
      <div
        ref={trackRef}
        style={view ? { ...trackStyle, transform: parked, willChange: 'transform' } : trackStyle}
        data-testid='table-carousel-track'
        data-panels={panels.join(',')}
        data-held-panels={held.join(',')}>
        {domOrder.map((id) => {
          const i = panels.indexOf(id);
          const onScreen = i !== -1;
          return (
            <div
              key={id}
              style={
                onScreen
                  ? { ...panelStyle, width: `${panelPct}%`, left: `${i * panelPct}%` }
                  : { ...heldPanelStyle, width: `${panelPct}%` }
              }
              aria-hidden={onScreen ? undefined : true}
              data-table-panel={id}
              data-table-panel-held={onScreen ? undefined : 'true'}>
              {/* Whether the player can SEE this panel. Held panels are parked
                  off-track and `visibility: hidden`, so anything that starts a
                  clock or escalates on a table's behalf must stand down here —
                  `isActiveTable` cannot say so on its own, because in a tiled
                  layout only one of the several VISIBLE panels is the active
                  one. See contexts/TableOnScreenContext. */}
              <TableOnScreenProvider value={onScreen}>
                {/* A tile in plain view keeps animating even when it is not
                    the focused one (EN-11150). See hooks/useTableSnap. */}
                <VisibleTileProvider value={perView > 1 && onScreen}>
                  <PanelContent id={id} render={children} />
                </VisibleTileProvider>
              </TableOnScreenProvider>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const viewportStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden'
};

// No transform / will-change at rest — see the component docblock.
const trackStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0
};

// Every panel is a full-viewport box, in every state — so a table's felt never
// sees a width change (and never re-scales) when a movement starts or ends.
const panelStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  width: '100%'
};

// A kept-alive panel: same full-viewport box (so its felt keeps its measured
// size and nothing re-scales when it comes back), parked well beyond the track's
// travel, unpainted and untouchable.
const heldPanelStyle: CSSProperties = {
  ...panelStyle,
  left: '300%',
  visibility: 'hidden',
  pointerEvents: 'none'
};

export default RebindingTableView;
